import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut } from "@/components/ui/command";
import { LayoutDashboard, Users, MessageSquare, Settings, CalendarCheck, Receipt, FileText, Map, FileSignature, ClipboardList, Layers, Megaphone, Server, Cloud, Calculator, Smartphone, PlayCircle, Rocket, Trophy, Zap, ClipboardCheck, Route, Wrench, DollarSign, Wand2, MapPin, Package, IdCard, Palette, Star, Flag, AlertTriangle, Camera, Sparkles } from "lucide-react";

/**
 * ⌘K / Ctrl+K command palette. Types 2 letters and jumps to any page.
 * Mount once inside DashboardShell.
 */
const ROUTES = [
  { group: "Daily Ops", to: "/dashboard", label: "Overview / Advisors", keywords: "advisors kpi", icon: LayoutDashboard },
  { group: "Daily Ops", to: "/how-it-works", label: "How It Works", keywords: "intro tutorial", icon: PlayCircle },
  { group: "Daily Ops", to: "/pitch", label: "Live Pitch (4-Beat)", keywords: "sales demo", icon: Rocket },
  { group: "Daily Ops", to: "/customer-journey", label: "Customer Journey", keywords: "flow", icon: Route },
  { group: "Daily Ops", to: "/examples", label: "Example SMS Coupon", keywords: "sample", icon: Smartphone },
  { group: "Daily Ops", to: "/campaigns?t=prospect", label: "Fast Services", keywords: "campaigns blast", icon: Zap },
  { group: "Daily Ops", to: "/conquest", label: "Conquest Customers", keywords: "map radius", icon: MapPin },
  { group: "Daily Ops", to: "/parts", label: "Parts Pipeline", keywords: "inventory", icon: Package },
  { group: "Daily Ops", to: "/body-shop", label: "Body Shop", keywords: "collision", icon: Palette },
  { group: "PreOwned Pipeline", to: "/posse", label: "Pipeline Board", keywords: "recon used", icon: Star },
  { group: "PreOwned Pipeline", to: "/recon-recovery", label: "Delayed Vehicles", keywords: "past due", icon: AlertTriangle },
  { group: "PreOwned Pipeline", to: "/manager-checkin", label: "Manager Check-In", keywords: "nightly", icon: ClipboardCheck },
  { group: "PreOwned Pipeline", to: "/photo-pipeline", label: "Photo Pipeline", keywords: "photos", icon: Camera },
  { group: "Daily Ops", to: "/segments", label: "Segments", keywords: "conquest", icon: Layers },
  { group: "Daily Ops", to: "/technician-dashboard", label: "Tech Dashboard", keywords: "flag hours", icon: Wrench },
  { group: "Daily Ops", to: "/sms", label: "SMS Sent Log", keywords: "messages threads", icon: MessageSquare },
  { group: "Daily Ops", to: "/appointments", label: "Appointments", keywords: "calendar ics", icon: CalendarCheck },
  { group: "Daily Ops", to: "/business-card", label: "Business Card", keywords: "contact", icon: IdCard },
  { group: "Daily Ops", to: "/settings", label: "Settings", keywords: "config dealership brand", icon: Settings },
  { group: "Daily Ops", to: "/roi", label: "ROI Calculator", keywords: "value roi", icon: Calculator },
  { group: "Pitch", to: "/decline-drive-letter", label: "Decline Drive Letter", keywords: "pitch letter", icon: FileText },
  { group: "Pitch", to: "/one-pager", label: "One-Pager", keywords: "pdf pitch", icon: FileText },
  { group: "Pitch", to: "/flyer", label: "DD Flyer", keywords: "flyer marketing", icon: FileText },
  { group: "Software", to: "/clouds", label: "Clouds", keywords: "architecture", icon: Cloud },
  { group: "Software", to: "/specs", label: "Specs", keywords: "spec sheet", icon: ClipboardList },
  { group: "Software", to: "/api-letter", label: "API Letter", keywords: "api integration", icon: Server },
  { group: "Software", to: "/roadmap", label: "Roadmap to Get Data", keywords: "roadmap", icon: Map },
  { group: "Onboarding", to: "/onboarding", label: "Onboarding Wizard", keywords: "first run setup", icon: Sparkles },
  { group: "Onboarding", to: "/contract", label: "Contract", keywords: "sign agreement", icon: FileSignature },
  { group: "Onboarding", to: "/statement", label: "Monthly Statement", keywords: "invoice billing", icon: Receipt },
  { group: "Coming", to: "/mpi", label: "MPI Inspection", keywords: "multi point", icon: ClipboardCheck },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = useCallback((to) => {
    setOpen(false);
    navigate(to);
  }, [navigate]);

  const [platformAdmin, setPlatformAdmin] = useState(false);
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => {
      api.get("/platform/me").then((r) => setPlatformAdmin(!!r.data?.is_platform_admin)).catch(() => {});
    });
  }, []);

  const groups = ROUTES.reduce((acc, r) => {
    (acc[r.group] = acc[r.group] || []).push(r);
    return acc;
  }, {});

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Jump to any page — type 2 letters…" data-testid="cmdk-input" />
        <CommandList data-testid="cmdk-list">
          <CommandEmpty>Nothing matches. Try &quot;posse&quot;, &quot;sms&quot;, or &quot;statement&quot;.</CommandEmpty>
          {Object.entries(groups).map(([g, items]) => (
            <CommandGroup heading={g} key={g}>
              {items.map((r) => {
                const Icon = r.icon;
                return (
                  <CommandItem key={r.to} value={`${r.label} ${r.keywords}`} onSelect={() => go(r.to)} data-testid={`cmdk-item-${r.to.replace(/[^a-z0-9]/gi, "-")}`}>
                    <Icon className="w-4 h-4 mr-2" strokeWidth={1.75} />
                    <span>{r.label}</span>
                    <CommandShortcut className="text-[0.65rem]">{r.to}</CommandShortcut>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
          {platformAdmin && (
            <CommandGroup heading="Platform">
              <CommandItem value="Platform admin dashboard" onSelect={() => go("/platform")} data-testid="cmdk-item-platform">
                <Trophy className="w-4 h-4 mr-2" strokeWidth={1.75} />
                <span>Platform Admin</span>
                <CommandShortcut className="text-[0.65rem]">/platform</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
